import { getUrlSegments } from './config';

/**
 * Checks whether a type is a function.
 * Extracted from https://stackoverflow.com/a/7356528.
 * @param {Function} functionToCheck
 * @return {boolean}
 * */
export function isFunction(functionToCheck) {
  return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
}

/**
 * Validates that a given route has the proper formatting,
 * expected by the router.
 *
 * It does NOT check the return value, as we don't want to call the matchers
 * more than necessary.
 * @param {object} route
 * @throws {TypeError}
 * */
export function validateRouteFormat(route) {
  if (!isFunction(route.matcher)) {
    throw new TypeError(`Route: ${route}, is missing required parameter 'matcher' or matcher is not a function`);
  }

  if (!isFunction(route.handler)) {
    throw new TypeError(`Route: ${route} is missing required parameter 'handler' or handler is not a function`);
  }
}

/**
 * Provides a more declarative and dynamic way of defining routes
 * to different pages, as opposed to using `switch` or `if` statements.
 *
 * Expects a list of routes in the following format:
 * [
 *   {
 *     matcher: (urlSegments) => boolean, // true => run this route
 *     handler: (urlSegments) => void
 *   }
 * ]
 *
 * @param {array} routes Ordered list of route objects
 * @param {string} uri Uri used to compare matchers with
 * @return {void}
 * */
export default function router(routes, uri) {
  routes.forEach(validateRouteFormat); // Complain if we find a malformed route

  const segments = getUrlSegments(uri);

  // Avoid to use for..of in JavaScript, as this fails on some browsers
  for (let i = 0; i < routes.length; i += 1) {
    const route = routes[i];

    if (route.matcher(segments) === true) { // Only allow boolean `true`
      route.handler(segments);
      break; // We don't want to run multiple routes on a page
    }
  }
}
